import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
} from "react";

interface TitleConfig {
  /** App name prefix (e.g., "MyApp" → "MyApp - Page Title") */
  appName?: string;
  /** Separator between app name and page title (default: " - ") */
  separator?: string;
}

const TitleConfigContext = createContext<TitleConfig>({});

/**
 * Provider to configure app-wide title settings.
 *
 * @example
 * ```tsx
 * <TitleConfigProvider appName="MyApp">
 *   <App />
 * </TitleConfigProvider>
 * ```
 */
export function TitleConfigProvider({
  children,
  appName,
  separator = " - ",
}: TitleConfig & { children: ReactNode }) {
  return (
    <TitleConfigContext.Provider value={{ appName, separator }}>
      {children}
    </TitleConfigContext.Provider>
  );
}

/**
 * Hook to set the document title dynamically.
 * Uses app name from TitleConfigProvider if available.
 *
 * @example
 * ```tsx
 * // With TitleConfigProvider appName="MyApp"
 * useSetTitle("Dashboard");  // → "MyApp - Dashboard"
 * useSetTitle();             // → "MyApp"
 *
 * // Without provider
 * useSetTitle("Dashboard");  // → "Dashboard"
 * ```
 */
export function useSetTitle(title?: string) {
  const { appName, separator = " - " } = useContext(TitleConfigContext);

  useLayoutEffect(() => {
    if (title && appName) {
      document.title = `${appName}${separator}${title}`;
    } else if (title) {
      document.title = title;
    } else if (appName) {
      document.title = appName;
    }
  }, [title, appName, separator]);
}
